import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  claimNextItemAPI,
  claimSpecificItemAPI,
  assignItemAPI,
  skipItemAPI,
  deleteItemAPI,
  fetchQueueListAPI,
  fetchMatterIdentityAPI,
  searchMattersAPI,
  fetchIntakeStatsAPI,
  fetchIntakeCountsAPI,
  fetchIntakeItemAPI,
} from "../service/mailIntake";

// Flipped to false now that the backend mail-intake endpoints are live on
// stable-staging (PR #27 + #28). Leaving the mock path intact below (guarded
// by this flag) so local dev without a backend still works: flip back to
// true temporarily if iterating on the frontend only.
const USE_MOCK = false;

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const MOCK_MATTERS = [
  { id: "m-3064", client_name: "Gary Todd", case_number: "VA-2025-3064", practice_area: "VA Disability" },
  { id: "m-4421", client_name: "James Thompson", case_number: "VA-2025-4421", practice_area: "VA Disability" },
  { id: "m-4422", client_name: "Maria Rodriguez", case_number: "VA-2025-4422", practice_area: "VA Disability" },
  { id: "m-4423", client_name: "Robert Chen", case_number: "VA-2025-4423", practice_area: "VA Disability" },
  { id: "m-4424", client_name: "Sarah Williams", case_number: "VA-2025-4424", practice_area: "VA Disability" },
  { id: "m-3891", client_name: "David Martinez", case_number: "VA-2024-3891", practice_area: "VA Disability" },
  { id: "m-4425", client_name: "Patricia Johnson", case_number: "VA-2025-4425", practice_area: "Personal Injury" },
  { id: "m-3720", client_name: "Michael Davis", case_number: "VA-2024-3720", practice_area: "VA Disability" },
  { id: "m-4426", client_name: "Jennifer Lee", case_number: "VA-2025-4426", practice_area: "VA Disability" },
  { id: "m-4427", client_name: "Christopher Brown", case_number: "VA-2025-4427", practice_area: "VA Disability" },
  { id: "m-3655", client_name: "Amanda Wilson", case_number: "VA-2024-3655", practice_area: "Personal Injury" },
  { id: "m-4428", client_name: "Patricia Johnson", case_number: "VA-2025-4428", practice_area: "VA Disability" },
  { id: "m-4429", client_name: "David Martinez", case_number: "VA-2025-4429", practice_area: "Personal Injury" },
];

const generateAutoMatch = () => {
  const roll = Math.random();
  if (roll < 0.6) {
    const matter = MOCK_MATTERS[Math.floor(Math.random() * MOCK_MATTERS.length)];
    return {
      type: "single_matter",
      client_name: matter.client_name,
      client_id: matter.id.replace("m-", "c-"),
      matters: [matter],
      confidence: Math.round(85 + Math.random() * 14),
      matched_on: ["veteran_name", "va_file_number"],
    };
  }
  if (roll < 0.8) {
    const multiClients = ["Patricia Johnson", "David Martinez"];
    const client = multiClients[Math.floor(Math.random() * multiClients.length)];
    const matters = MOCK_MATTERS.filter((m) => m.client_name === client);
    return {
      type: "multi_matter",
      client_name: client,
      client_id: matters[0].id.replace("m-", "c-"),
      matters,
      confidence: Math.round(75 + Math.random() * 20),
      matched_on: ["veteran_name"],
    };
  }
  return { type: "no_match", client_name: null, client_id: null, matters: [], confidence: 0, matched_on: [] };
};

const generateMockItems = (count) => {
  const items = [];
  const sources = ["scan", "electronic"];
  for (let i = 1; i <= count; i++) {
    const daysAgo = Math.floor(Math.random() * 5);
    const isAssigned = Math.random() < 0.3;
    const isClaimed = !isAssigned && Math.random() < 0.1;
    // `created_at` is the field name the backend emits on
    // MailIntakeItem; the legacy mock alias `received_at` would mean
    // mock sort keys resolve to undefined once the default sort_by is
    // `created_at` (Bugbot PR #47). Keep both on the mock item so any
    // legacy consumer that still reads `received_at` doesn't break.
    const createdAt = new Date(
      Date.now() - daysAgo * 86400000 - Math.random() * 86400000
    ).toISOString();
    items.push({
      id: `intake-${String(i).padStart(5, "0")}`,
      file_name: `scan_batch_${new Date(Date.now() - daysAgo * 86400000).toISOString().slice(0, 10).replace(/-/g, "")}_${String(i).padStart(3, "0")}.pdf`,
      original_filename: `scan_batch_${new Date(Date.now() - daysAgo * 86400000).toISOString().slice(0, 10).replace(/-/g, "")}_${String(i).padStart(3, "0")}.pdf`,
      file_size: Math.floor(100000 + Math.random() * 3000000),
      file_type: "application/pdf",
      created_at: createdAt,
      received_at: createdAt,
      source: sources[Math.floor(Math.random() * sources.length)],
      page_count: Math.ceil(Math.random() * 30),
      status: isAssigned ? "assigned" : isClaimed ? "claimed" : "pending",
      claimed_by: isClaimed ? "other-user" : null,
      claimed_at: isClaimed ? new Date().toISOString() : null,
      assigned_matter: isAssigned ? MOCK_MATTERS[Math.floor(Math.random() * MOCK_MATTERS.length)] : null,
      assigned_by: isAssigned ? "Current User" : null,
      assigned_at: isAssigned ? new Date(Date.now() - Math.random() * 86400000).toISOString() : null,
      auto_match: generateAutoMatch(),
    });
  }
  return items;
};

let MOCK_ITEMS = generateMockItems(247);
let mockClaimIndex = 0;

const MOCK_SORTERS = [
  { id: "u-1", name: "Alex Rivera", avatar: "AR" },
  { id: "u-2", name: "Jordan Kim", avatar: "JK" },
  { id: "u-3", name: "Casey Morgan", avatar: "CM" },
  { id: "u-4", name: "Taylor Brooks", avatar: "TB" },
];

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const claimNextItem = createAsyncThunk(
  "mailIntake/claimNext",
  async (_, { rejectWithValue }) => {
    try {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 250));
        const available = MOCK_ITEMS.filter((i) => i.status === "pending");
        if (available.length === 0) return { data: { item: null } };
        const item = available[mockClaimIndex % available.length];
        mockClaimIndex++;
        item.status = "claimed";
        item.claimed_by = "current-user";
        item.claimed_at = new Date().toISOString();
        return { data: { item } };
      }
      // Backend returns 200 with an envelope: { item: MailIntakeItem | null }.
      // Empty queue is a null item, NOT an HTTP error — see PR #27.
      const response = await claimNextItemAPI();
      return { data: { item: response?.data?.item ?? null } };
    } catch (error) {
      return rejectWithValue(error.message || "No documents available");
    }
  }
);

// Claim a specific intake item by id (as opposed to "next available").
// Wired to the List tab's row-click handler so a sorter can jump
// straight from a queue row into the Assign view for that document.
//
// Backend semantics (POST /mail-intake/<id>/claim/):
//   - PENDING / SKIPPED  -> claim and return the item
//   - CLAIMED by self    -> idempotent, return the item
//   - CLAIMED by other   -> 409 with the other sorter's name
//   - ASSIGNED / DELETED -> 409 (terminal state, can't re-open here)
//   - UPLOADING          -> 409 (still in flight)
// Empty `currentItem` on any non-success path so the Assign view doesn't
// show stale state.
export const claimSpecificItem = createAsyncThunk(
  "mailIntake/claimSpecific",
  async (itemId, { rejectWithValue }) => {
    try {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 150));
        const item = MOCK_ITEMS.find((i) => i.id === itemId);
        if (!item) return rejectWithValue("Item not found");
        if (item.status === "assigned" || item.status === "deleted") {
          return rejectWithValue("Item is already in a terminal state");
        }
        item.status = "claimed";
        item.claimed_by = "current-user";
        item.claimed_at = new Date().toISOString();
        return { data: { item } };
      }
      const response = await claimSpecificItemAPI(itemId);
      return { data: { item: response?.data?.item ?? null } };
    } catch (error) {
      return rejectWithValue(error.message || "Could not open this document");
    }
  }
);

// Silent refresh of the currently-open item while its Phase A pipeline
// is still running (extraction in flight when the sorter claimed it).
// The Assign view polls this so the Markdown tab flips from "still
// reading" to the extracted text — and the auto-match suggestion lands
// — without the sorter having to skip/re-claim. The fulfilled reducer
// only applies the payload if the item on screen is still the same one,
// so a poll response can never clobber a claim/skip that raced it.
export const refreshCurrentItem = createAsyncThunk(
  "mailIntake/refreshCurrentItem",
  async (itemId, { rejectWithValue }) => {
    try {
      const response = await fetchIntakeItemAPI(itemId);
      return { data: { item: response?.data ?? null } };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to refresh item");
    }
  }
);

export const assignItem = createAsyncThunk(
  "mailIntake/assign",
  async ({ itemId, matterId, documentName }, { rejectWithValue }) => {
    try {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 200));
        const item = MOCK_ITEMS.find((i) => i.id === itemId);
        if (item) {
          const matter = MOCK_MATTERS.find((m) => m.id === matterId);
          item.status = "assigned";
          item.assigned_matter = matter;
          item.assigned_by = "Current User";
          item.assigned_at = new Date().toISOString();
          item.claimed_by = null;
          item.claimed_at = null;
          if (documentName) item.file_name = documentName;
        }
        return { data: { success: true, item_id: itemId, matter_id: matterId } };
      }
      const response = await assignItemAPI({ itemId, matterId, documentName });
      return { data: response?.data };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to assign");
    }
  }
);

export const skipItem = createAsyncThunk(
  "mailIntake/skip",
  async (itemId, { rejectWithValue }) => {
    try {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 100));
        const item = MOCK_ITEMS.find((i) => i.id === itemId);
        if (item) {
          item.status = "pending";
          item.claimed_by = null;
          item.claimed_at = null;
        }
        return { data: { success: true } };
      }
      const response = await skipItemAPI(itemId);
      return { data: response?.data };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to skip");
    }
  }
);

export const deleteItem = createAsyncThunk(
  "mailIntake/delete",
  async (itemId, { rejectWithValue }) => {
    try {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 150));
        MOCK_ITEMS = MOCK_ITEMS.filter((i) => i.id !== itemId);
        return { data: { success: true } };
      }
      const response = await deleteItemAPI(itemId);
      return { data: response?.data };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete");
    }
  }
);

export const fetchQueueList = createAsyncThunk(
  "mailIntake/fetchQueue",
  async (_, { getState, rejectWithValue }) => {
    try {
      const filters = getState().mailIntake.queueFilters;
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 250));
        let filtered = [...MOCK_ITEMS];
        // "ACTIVE" / "ALL" / empty are all sentinel values meaning "no
        // explicit status filter" -- the backend equivalent is omitting
        // the status query param entirely. We retain ALL for back-compat
        // with anything that still injects the old value.
        const statusFilter = filters.status;
        const isSentinel =
          !statusFilter || statusFilter === "ACTIVE" || statusFilter === "ALL";
        if (!isSentinel) {
          filtered = filtered.filter((i) => i.status === statusFilter);
        }
        if (filters.search) {
          const q = filters.search.toLowerCase();
          filtered = filtered.filter(
            (i) =>
              i.file_name.toLowerCase().includes(q) ||
              i.assigned_matter?.client_name?.toLowerCase().includes(q) ||
              i.auto_match?.client_name?.toLowerCase().includes(q)
          );
        }
        if (filters.sort_by) {
          filtered.sort((a, b) => {
            let aVal = a[filters.sort_by];
            let bVal = b[filters.sort_by];
            // Date fields need Date() coercion for correct ordering.
            // `received_at` stays in the list for back-compat with any
            // older mock data that still uses that alias (Bugbot PR #47).
            const isDateField =
              filters.sort_by === "created_at" ||
              filters.sort_by === "received_at";
            if (isDateField) {
              aVal = new Date(aVal);
              bVal = new Date(bVal);
            }
            if (aVal < bVal) return filters.sort_direction === "desc" ? 1 : -1;
            if (aVal > bVal) return filters.sort_direction === "desc" ? -1 : 1;
            return 0;
          });
        }
        const total = filtered.length;
        const start = (filters.page - 1) * filters.page_size;
        const results = filtered.slice(start, start + filters.page_size);
        const counts = {
          all: MOCK_ITEMS.length,
          pending: MOCK_ITEMS.filter((i) => i.status === "pending").length,
          claimed: MOCK_ITEMS.filter((i) => i.status === "claimed").length,
          assigned: MOCK_ITEMS.filter((i) => i.status === "assigned").length,
        };
        return { data: { results, count: total, counts } };
      }
      const response = await fetchQueueListAPI(filters);
      return { data: response?.data };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch queue");
    }
  }
);

export const searchMatters = createAsyncThunk(
  "mailIntake/searchMatters",
  async (query, { rejectWithValue }) => {
    try {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 200));
        const q = query.toLowerCase();
        const results = MOCK_MATTERS.filter(
          (m) =>
            m.client_name.toLowerCase().includes(q) ||
            m.case_number.toLowerCase().includes(q)
        );
        return { data: { results } };
      }
      const response = await searchMattersAPI(query);
      const results = response?.data?.results || [];
      return { data: { results } };
    } catch (error) {
      return rejectWithValue(error.message || "Search failed");
    }
  }
);

// Client identity card for the Assign screen (Matthew, 2026-07-18):
// once a matter is chosen, show the client's stored identifiers
// (address, phone, SSN last 4, VA file #, DOB) so the human can
// confirm the match against the letter.
export const fetchMatterIdentity = createAsyncThunk(
  "mailIntake/fetchMatterIdentity",
  async (matterId, { rejectWithValue }) => {
    try {
      const response = await fetchMatterIdentityAPI(matterId);
      return { data: response?.data || null };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load client record");
    }
  }
);

// Backend /mail-intake/counts/ returns one number per Status plus aggregate
// fields:
//   { uploading, pending, claimed, skipped, assigned, deleted,
//     active, total }
// where `active = uploading + pending + claimed + skipped` matches the
// default scope of /mail-intake/queue/. The List tab badge reads
// `counts.active` (the work the sorter can still act on), the Assign tab
// reads `counts.pending`, and `counts.all` is kept as a synonym for the
// historical total so older dashboard widgets keep working.
//
// We compute `active` client-side as a fallback for older backend builds
// that don't yet emit it (deploys aren't atomic across services).
export const refreshIntakeCounts = createAsyncThunk(
  "mailIntake/refreshCounts",
  async (_, { rejectWithValue }) => {
    try {
      if (USE_MOCK) {
        const pending = MOCK_ITEMS.filter((i) => i.status === "pending").length;
        const claimed = MOCK_ITEMS.filter((i) => i.status === "claimed").length;
        const skipped = MOCK_ITEMS.filter((i) => i.status === "skipped").length;
        const uploading = MOCK_ITEMS.filter((i) => i.status === "uploading").length;
        const assigned = MOCK_ITEMS.filter((i) => i.status === "assigned").length;
        return {
          data: {
            all: MOCK_ITEMS.length,
            // Match the real-fallback formula and the backend's
            // _ACTIVE_QUEUE_STATUSES exactly. Today the mock generator
            // only emits pending/claimed/assigned, so skipped + uploading
            // are zero, but expressing the formula in full keeps the
            // mock and the real path semantically identical so future
            // mock-data changes can't silently drift (Bugbot PR #47).
            active: pending + claimed + skipped + uploading,
            pending,
            claimed,
            assigned,
            skipped,
            deleted: 0,
            uploading,
          },
        };
      }
      const response = await fetchIntakeCountsAPI();
      const raw = response?.data || {};
      const pending = raw.pending ?? 0;
      const claimed = raw.claimed ?? 0;
      const skipped = raw.skipped ?? 0;
      const uploading = raw.uploading ?? 0;
      return {
        data: {
          all: raw.total ?? 0,
          active: raw.active ?? pending + claimed + skipped + uploading,
          pending,
          claimed,
          assigned: raw.assigned ?? 0,
          skipped,
          deleted: raw.deleted ?? 0,
          uploading,
        },
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to refresh counts");
    }
  }
);

export const fetchStats = createAsyncThunk(
  "mailIntake/fetchStats",
  async ({ period = "today", date = "" } = {}, { rejectWithValue }) => {
    try {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 300));
        const genSorterStats = (sorter) => {
          const docsToday = Math.floor(40 + Math.random() * 120);
          const avgTime = Math.round(15 + Math.random() * 45);
          return {
            ...sorter,
            user_id: sorter.id,
            docs_processed: docsToday,
            avg_seconds_per_doc: avgTime,
            docs_per_hour: Math.round(3600 / avgTime),
          };
        };
        const received = Math.floor(300 + Math.random() * 900);
        return {
          data: {
            period: date ? "date" : period,
            date: date || null,
            summary: {
              received_period: received,
              received_by_source: { scanned_mail: received - 40, fax: 40 },
              assigned_period: Math.floor(200 + Math.random() * 300),
              assigned_with_suggestion: Math.floor(150 + Math.random() * 100),
              pending: MOCK_ITEMS.filter((i) => i.status === "pending").length,
              total_queue: MOCK_ITEMS.length,
              queue_now: { by_status: {}, by_pipeline_status: {} },
            },
            daily: [],
            sorters: MOCK_SORTERS.map(genSorterStats),
          },
        };
      }
      const response = await fetchIntakeStatsAPI({ period, date });
      return { data: response?.data };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch stats");
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const MailIntakeSlice = createSlice({
  name: "mailIntake",
  initialState: {
    currentItem: null,
    currentItemLoading: false,
    noMoreItems: false,
    assigning: false,
    // Latest-request-wins guard for the silent poll (see
    // refreshCurrentItem reducers).
    latestRefreshRequestId: null,

    matterSearchResults: [],
    matterIdentity: null,
    matterIdentityLoading: false,
    matterSearchLoading: false,

    queue: {},
    queueLoading: false,
    queueFilters: {
      page: 1,
      page_size: 25,
      // "ACTIVE" is a client-side sentinel meaning "everything the sorter
      // can still act on". The service layer translates it into a request
      // with no status param, which the backend interprets as the active
      // queue (UPLOADING + PENDING + CLAIMED + SKIPPED). Assigned and
      // deleted rows are reachable by picking those values in the
      // dropdown.
      status: "ACTIVE",
      search: "",
      // Must match the backend field name on `MailIntakeItem` and the
      // ListView column's dataIndex (both `created_at`). Using a stale
      // alias here would silently break server-side sorting once the
      // queue API starts honoring `sort_by` (Bugbot PR #47).
      sort_by: "created_at",
      sort_direction: "desc",
    },
    // Lifted out of MailIntake.js so that any child view (e.g. ListView's
    // row-click handler) can switch tabs without prop-drilling. MailIntake
    // reads + writes this through the `setActiveTab` action below; the
    // page-level useState is replaced by a useSelector binding.
    activeTab: "assign",
    counts: {},

    stats: {},
    statsLoading: false,
  },
  reducers: {
    setQueueFilters: (state, action) => {
      state.queueFilters = { ...state.queueFilters, ...action.payload };
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
      state.noMoreItems = false;
    },
    clearMatterSearch: (state) => {
      state.matterSearchResults = [];
      state.matterIdentity = null;
    },
    // Cross-component tab navigation. Used by ListView's row click to
    // jump straight to the Assign tab once a specific item has been
    // claimed for the current user.
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Claim next
      .addCase(claimNextItem.pending, (state) => {
        state.currentItemLoading = true;
        state.noMoreItems = false;
      })
      .addCase(claimNextItem.fulfilled, (state, action) => {
        const item = action.payload?.data?.item ?? null;
        state.currentItem = item;
        state.currentItemLoading = false;
        state.noMoreItems = !item;
      })
      .addCase(claimNextItem.rejected, (state) => {
        state.currentItem = null;
        state.currentItemLoading = false;
        state.noMoreItems = true;
      })

      // Silent poll refresh: apply only if the sorter is still looking
      // at the same item (a skip/claim while the request was in flight
      // must win) AND this response is from the newest poll request —
      // latest-request-wins, so a slow, older GET can never roll the
      // item back to "still reading" after a fresher one landed
      // (Bugbot #200). Never touches loading flags — this is a
      // background update, not a navigation.
      .addCase(refreshCurrentItem.pending, (state, action) => {
        state.latestRefreshRequestId = action.meta.requestId;
      })
      .addCase(refreshCurrentItem.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestRefreshRequestId) {
          return; // stale response — a newer poll is already in flight/landed
        }
        const item = action.payload?.data?.item ?? null;
        if (item && state.currentItem && state.currentItem.id === item.id) {
          state.currentItem = item;
        }
      })

      // Claim specific (row-click in List). Distinct from claim-next:
      // a rejection here is a *real* error (item already claimed by
      // someone else, terminal status, etc.), not a "queue is empty"
      // signal -- so we leave noMoreItems alone and surface the error
      // via rejectWithValue for the caller to toast.
      .addCase(claimSpecificItem.pending, (state) => {
        state.currentItemLoading = true;
      })
      .addCase(claimSpecificItem.fulfilled, (state, action) => {
        const item = action.payload?.data?.item ?? null;
        state.currentItem = item;
        state.currentItemLoading = false;
        state.noMoreItems = false;
      })
      .addCase(claimSpecificItem.rejected, (state) => {
        state.currentItemLoading = false;
      })

      // Assign
      .addCase(assignItem.pending, (state) => {
        state.assigning = true;
      })
      .addCase(assignItem.fulfilled, (state) => {
        state.assigning = false;
        state.currentItem = null;
      })
      .addCase(assignItem.rejected, (state) => {
        state.assigning = false;
      })

      // Skip
      .addCase(skipItem.fulfilled, (state) => {
        state.currentItem = null;
      })

      // Delete
      .addCase(deleteItem.fulfilled, (state) => {
        state.currentItem = null;
      })

      // Queue list
      .addCase(fetchQueueList.pending, (state) => {
        state.queueLoading = true;
      })
      .addCase(fetchQueueList.fulfilled, (state, action) => {
        state.queue = action.payload.data;
        if (action.payload.data.counts) {
          state.counts = action.payload.data.counts;
        }
        state.queueLoading = false;
      })
      .addCase(fetchQueueList.rejected, (state) => {
        state.queueLoading = false;
        state.queue = { results: [], count: 0 };
      })

      // Matter search
      .addCase(searchMatters.pending, (state) => {
        state.matterSearchLoading = true;
      })
      .addCase(searchMatters.fulfilled, (state, action) => {
        state.matterSearchResults = action.payload.data.results;
        state.matterSearchLoading = false;
      })
      .addCase(searchMatters.rejected, (state) => {
        state.matterSearchLoading = false;
        state.matterSearchResults = [];
      })

      .addCase(fetchMatterIdentity.pending, (state) => {
        state.matterIdentityLoading = true;
      })
      .addCase(fetchMatterIdentity.fulfilled, (state, action) => {
        state.matterIdentity = action.payload?.data || null;
        state.matterIdentityLoading = false;
      })
      .addCase(fetchMatterIdentity.rejected, (state) => {
        state.matterIdentity = null;
        state.matterIdentityLoading = false;
      })

      // Counts (tab badges)
      .addCase(refreshIntakeCounts.fulfilled, (state, action) => {
        state.counts = action.payload.data;
      })

      // Stats
      .addCase(fetchStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.stats = action.payload.data;
        state.statsLoading = false;
      })
      .addCase(fetchStats.rejected, (state) => {
        state.statsLoading = false;
      });
  },
});

export const {
  setQueueFilters,
  clearCurrentItem,
  clearMatterSearch,
  setActiveTab,
} = MailIntakeSlice.actions;
export default MailIntakeSlice.reducer;
